import React, { FC, useRef, useState } from "react";
import { Grid, Stack, Typography } from "@mui/material";
import { InnerBox } from "@/components/elements/InnerBox";
import { Button } from "@/components/elements/Button";
import { useFormContext } from "react-hook-form";
import { TCompanyForm } from "@/features/company/types/CompanyForm";
import { FileItem } from "@/components/elements/FileItem";
import { ACCEPT_FILE_TYPES, ACCEPT_STRING, MAX_FILE_CONTRACT_UPLOAD, MAX_SIZE } from "@/features/company/constants/contractFile";
import { toast } from "@/utils/toast";
import { Input } from "@/components/elements/Input";
import { FormField } from "@/components/elements/FormField";
import { CompanyFormTitle } from "@/features/company/components/company-info/CompanyFormTitle";
import { Modal } from "@/components/elements/Modal";
import { useDialog } from "@/hooks/useDialog";
import FileUploadIcon from "@mui/icons-material/FileUpload";

type TContractInfo = {
  readonly?: boolean;
};

/** Extract display name from filePath, removing timestamp prefix */
const getFileName = (filePath: string) => {
  const segments = filePath.split("/");
  const fullName = segments[segments.length - 1] || filePath;
  const underscoreIdx = fullName.indexOf("_");
  return underscoreIdx > 0 ? fullName.substring(underscoreIdx + 1) : fullName;
};

export const ContractInfo: FC<TContractInfo> = ({ readonly }) => {
  const { control, watch, setValue, getValues } = useFormContext<TCompanyForm>();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const contractFiles = watch("contractFiles") ?? [];
  const contractPaths = watch("contractPaths") ?? [];

  const confirmDeleteDialog = useDialog();
  const [pendingDeleteFileName, setPendingDeleteFileName] = useState("");
  const pendingDeleteAction = useRef<(() => void) | null>(null);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const newFiles = Array.from(e.target.files);
    e.target.value = "";

    const currentFiles = getValues("contractFiles") ?? [];
    const currentFilePaths = getValues("contractPaths") ?? [];
    const totalFile = newFiles.length + currentFilePaths.length + currentFiles.length;
    const mergedFiles = [...currentFiles, ...newFiles];

    if (totalFile > MAX_FILE_CONTRACT_UPLOAD) {
      toast.error(`ファイルは最大${MAX_FILE_CONTRACT_UPLOAD}件までアップロードできます。`);
      return;
    }

    const listFileErrorSize: string[] = [];
    const listFileErrorType: string[] = [];

    newFiles.forEach((file) => {
      if (file.size > MAX_SIZE) {
        listFileErrorSize.push(file.name);
      }
      const isValid = ACCEPT_FILE_TYPES.some((t) => {
        const ext = file.name.split(".").pop()?.toLowerCase();
        return (t.mimeType === file.type || t.extensions.includes(ext ?? "")) ?? false;
      });
      if (!isValid) {
        listFileErrorType.push(file.name);
      }
    });

    if (listFileErrorType.length > 0) {
      const allowedExtensions = ACCEPT_FILE_TYPES.map((type) => type.extensions)
        .flat()
        .join("、");

      const errorType =
        `以下のファイル形式はアップロードできません。\n` +
        `${listFileErrorType.map((fileName) => `◦${fileName}`).join("\n")}\n` +
        `（アップロード可能なファイル形式は${allowedExtensions}です。）`;

      toast.error(errorType);
      return;
    }

    if (listFileErrorSize.length > 0) {
      const errorSize = `以下のファイルはサイズ上限（${MAX_SIZE / 1000}KB）を超えているため、アップロードできませんでした：
      ${listFileErrorSize.map((fileName) => `◦${fileName}`).join("\n")}
      `;

      toast.error(errorSize);
      return;
    }

    setValue("contractFiles", mergedFiles, { shouldValidate: true, shouldDirty: true });

    e.target.value = "";
  };

  const removeContractFile = (index: number) => {
    const currentFiles = getValues("contractFiles") ?? [];
    if (currentFiles.length > 0) {
      const newFiles = currentFiles.filter((_, i) => i !== index);
      setValue("contractFiles", newFiles, { shouldValidate: true, shouldDirty: true });
    }
  };

  const removeContractPath = (index: number) => {
    const currentPaths = getValues("contractPaths") ?? [];
    if (currentPaths.length > 0) {
      const newPaths = currentPaths.filter((_, i) => i !== index);
      setValue("contractPaths", newPaths, { shouldValidate: true, shouldDirty: true });
    }
  };

  const handleRemoveWithConfirm = async (fileName: string, removeAction: () => void) => {
    setPendingDeleteFileName(fileName);
    pendingDeleteAction.current = removeAction;
    const confirmed = await confirmDeleteDialog.show();
    if (confirmed) {
      removeAction();
    }
    pendingDeleteAction.current = null;
  };

  return (
    <Stack>
      <InnerBox title={<CompanyFormTitle text="基本契約書" />} showDivider={false}>
        <Grid container spacing={2}>
          <Grid item xs={9}>
            <FormField control={control} name="contractUrl">
              <Input readonly={readonly} />
            </FormField>
          </Grid>
          <Grid item xs={3}>
            <Button
              type="button"
              variant="outline"
              fullWidth
              onClick={handleUploadClick}
              disabled={contractFiles.length + contractPaths.length >= 5}
              sx={{
                marginTop: "16px",
                width: "100%",
              }}
            >
              <FileUploadIcon sx={{ marginTop: "3px" }} />
              契約書のアップロード
            </Button>
          </Grid>
          <input ref={fileInputRef} type="file" multiple hidden accept={ACCEPT_STRING} onChange={handleFileChange} />
          <Grid item xs={12}>
            {contractPaths.map((path, idx) => (
              <FileItem
                key={idx}
                filePath={path}
                downloadable
                onRemove={() => handleRemoveWithConfirm(getFileName(path), () => removeContractPath(idx))}
              />
            ))}
            {contractFiles.map((file, idx) => (
              <FileItem
                key={idx}
                file={file}
                downloadable
                onRemove={() => handleRemoveWithConfirm(file.name, () => removeContractFile(idx))}
              />
            ))}
          </Grid>
        </Grid>
      </InnerBox>

      <Modal
        isOpen={confirmDeleteDialog.open}
        onClose={confirmDeleteDialog.cancel}
        onOk={confirmDeleteDialog.confirm}
        labelClose="いいえ"
        labelOk="はい"
        width={500}
        title="削除の確認"
      >
        <Stack mb={2}>
          <Typography>「{pendingDeleteFileName}」を削除してもよろしいですか？</Typography>
        </Stack>
      </Modal>
    </Stack>
  );
};
